// Scan cover sheets: one per pile of paper, for an organization and its visit (or a date when there is no visit). Put it on top of
// the pile and scan; the app files each pile under its organization and visit. Any NGF staff member can print them.
//   /cover-sheets         choose the organization and visit, or one person, and print; recent sheets
//   /cover-sheets/print   the sheets, one per page (opens the browser's print dialog)
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { canImport, get, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { orgMatches } from '../lib/orgSearch.ts';
import { useData } from '../lib/useData.ts';

interface Org { id: string; name: string }
interface EventOpt { id: string; startsOn: string; endsOn: string | null; location: string | null; status: string }
interface Sheet {
  id: string; shortCode: string; departmentId: string | null; departmentName: string | null; eventId: string | null;
  participantId?: string | null; participantName?: string | null; participantDob?: string | null; assessmentId?: string | null; assessedOn?: string | null; eventStartsOn: string | null; eventEndsOn: string | null;
  eventLocation: string | null; sheetDate: string | null; note: string | null; printedBy: string | null; printedAt: string; voidedAt: string | null;
  usedCount: number; lastUsedAt: string | null; qrSvg?: string;
}
interface PersonHit { id: string; firstName: string; lastName: string; dob: string | null; departmentName: string | null }
interface PersonVisit { id: string; assessedOn: string; departmentName: string | null }
type PersonDetail = PersonHit & { history: { id: string; assessedOn: string; status: string; departmentName: string | null; supersededBy?: string | null }[] };

// Today here (not in UTC, which is already tomorrow on an evening in South Carolina).
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
const us = (iso: string | null) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');
const visitLabel = (e: { startsOn: string; endsOn: string | null; location: string | null }) =>
  `${us(e.startsOn)}${e.endsOn && e.endsOn !== e.startsOn ? `–${us(e.endsOn)}` : ''}${e.location ? ` · ${e.location}` : ''}`;

/** Make cover sheets and open the print page. */
export async function printCoverSheets(nav: (to: string) => void, sheets: { departmentId: string | null; eventId: string | null; sheetDate: string | null; note?: string | null;
  participantId?: string | null; assessmentId?: string | null }[]) {
  const r = await post<{ sheets: { id: string }[] }>('/api/cover-sheets', { sheets });
  nav(`/cover-sheets/print?ids=${r.sheets.map((s) => s.id).join(',')}`);
}

export function CoverSheets() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const orgs = useData<Org[]>('/api/departments');
  const recent = useData<Sheet[]>('/api/cover-sheets');
  // What the sheet is for: an organization (a pile from a visit, or paper with no visit) or one person.
  const [mode, setMode] = useState<'org' | 'person'>(params.get('participantId') ? 'person' : 'org');
  const [q, setQ] = useState('');
  const [org, setOrg] = useState<Org | null>(null);
  const [events, setEvents] = useState<EventOpt[] | null>(null);
  const [eventId, setEventId] = useState('');
  const [noVisit, setNoVisit] = useState(false);
  const [sheetDate, setSheetDate] = useState(today());
  const [note, setNote] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // One person: who, and which of their visits ('' = none).
  const [pq, setPq] = useState('');
  const [found, setFound] = useState<PersonHit[]>([]);
  const [person, setPerson] = useState<PersonHit | null>(null);
  const [visits, setVisits] = useState<PersonVisit[] | null>(null);
  const [visit, setVisit] = useState('');

  // Opened from an organization, visit or person page (Print cover sheet): start with it filled in.
  const wantOrg = params.get('departmentId'), wantEvent = params.get('eventId'), wantPerson = params.get('participantId');
  useEffect(() => {
    if (wantOrg && orgs.data && !org) { const o = orgs.data.find((x) => x.id === wantOrg); if (o) setOrg(o); }
  }, [wantOrg, orgs.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!wantPerson) return;
    get<PersonDetail>(`/api/participants/${wantPerson}`).then((p) => setPerson(p)).catch(setErr);
  }, [wantPerson]);

  const hits = useMemo(() => (q.trim().length >= 2 && !org ? (orgs.data ?? []).filter((o) => orgMatches(o.name, q)).slice(0, 8) : []), [q, org, orgs.data]);
  useEffect(() => {
    setEvents(null); setEventId('');
    if (!org) return;
    get<EventOpt[]>(`/api/cover-sheets/events?departmentId=${org.id}`).then((e) => {
      setEvents(e);
      // The visit it was opened from, else the nearest visit on or before today, else the first one.
      const now = today();
      const pick = e.find((x) => x.id === wantEvent) ?? e.find((x) => x.startsOn <= now) ?? e[0];
      setEventId(pick?.id ?? ''); setNoVisit(!e.length);
    }).catch(setErr);
  }, [org]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setVisits(null); setVisit('');
    if (!person) return;
    get<PersonDetail>(`/api/participants/${person.id}`).then((p) =>
      setVisits(p.history.filter((a) => a.status !== 'void' && !a.supersededBy).map((a) => ({ id: a.id, assessedOn: a.assessedOn, departmentName: a.departmentName }))))
      .catch(setErr);
  }, [person]);
  // People as you type (after a short pause); searchedFor says which text the list is for.
  const [searchedFor, setSearchedFor] = useState('');
  useEffect(() => {
    const text = pq.trim();
    if (person || text.length < 2) { setFound([]); setSearchedFor(''); return; }
    const t = setTimeout(() => {
      get<PersonHit[]>(`/api/participants?limit=20&q=${encodeURIComponent(text)}`).then((r) => { setFound(r); setSearchedFor(text); }).catch(setErr);
    }, 300);
    return () => clearTimeout(t);
  }, [pq, person]);

  // Make the one sheet and open its print page.
  const print = async () => {
    setErr(null);
    let sheet;
    if (mode === 'person') {
      if (!person) return;
      const v = visits?.find((a) => a.id === visit);
      sheet = { departmentId: null, participantId: person.id, assessmentId: v?.id ?? null, eventId: null, sheetDate: v ? null : sheetDate, note: note.trim() || null };
    } else {
      if (!org) return;
      const ev = events?.find((e) => e.id === eventId);
      if (!noVisit && !ev) return setErr(new Error('Choose the visit, or tick "No visit" and give a date.'));
      sheet = { departmentId: org.id, eventId: noVisit ? null : ev!.id, sheetDate: noVisit ? sheetDate : null, note: note.trim() || null };
    }
    setBusy(true);
    try { await printCoverSheets(nav, [sheet]); } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  const voidSheet = async (s: Sheet) => {
    if (!window.confirm(`Void cover sheet ${s.shortCode}? Scans with it on top will no longer be filed under ${s.participantName ?? s.departmentName}.`)) return;
    try { await post(`/api/cover-sheets/${s.id}/void`); await recent.reload(); } catch (x) { setErr(x); }
  };
  const ready = mode === 'person' ? !!person : !!org;

  return (
    <>
      <h1>Cover sheets</h1>
      <p className="muted" style={{ marginTop: 0 }}>Print a cover sheet for each pile of paper and put it on top before scanning. The app files each pile under its
        organization and visit (or its person), and lists it there right away. Several piles can go through the copier in one stack.
        <b> Print cover sheet</b> at the top right of an organization’s, a visit’s or a person’s page opens this page with it filled in.</p>
      <ErrorBox error={err ?? orgs.error} />
      <div className="card">
        <h2 style={{ marginTop: 0 }}>New cover sheet</h2>
        <div className="row" style={{ gap: 16, marginBottom: 8 }}>
          <label className="small" style={{ fontWeight: 400, flex: '0 0 auto' }}><input type="radio" checked={mode === 'org'} onChange={() => setMode('org')} /> An organization’s paper</label>
          <label className="small" style={{ fontWeight: 400, flex: '0 0 auto' }}><input type="radio" checked={mode === 'person'} onChange={() => setMode('person')} /> One person’s paper
            <span className="muted"> (late labs, a doctor’s note)</span></label>
        </div>
        {mode === 'org' ? (
        <div className="row">
          <div className="field org-picker"><label htmlFor="cs-org">Organization</label>
            {org ? <div className="row" style={{ alignItems: 'center', gap: 8 }}><b>{org.name}</b><button type="button" className="link" onClick={() => { setOrg(null); setQ(''); }}>change</button></div>
              : <><input id="cs-org" type="search" placeholder="Search organizations: type part of the name" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && hits[0]) { e.preventDefault(); setOrg(hits[0]); } }} />
                {hits.length > 0 && <ul className="org-picker-hits">{hits.map((o) => <li key={o.id}><button type="button" onClick={() => setOrg(o)}>{o.name}</button></li>)}</ul>}
                {q.trim().length >= 2 && !hits.length && <p className="small muted" style={{ margin: '0.3rem 0 0' }}>
                  {orgs.data ? `No organization matches “${q.trim()}”.` : 'Loading organizations…'}</p>}</>}
          </div>
          {org && <div className="field"><label htmlFor="cs-visit">Visit</label>
            {!noVisit ? <select id="cs-visit" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              {!events ? <option>Loading…</option> : !events.length ? <option value="">No visits in the last 6 months</option>
                : events.map((e) => <option key={e.id} value={e.id}>{visitLabel(e)}</option>)}</select>
              : <input id="cs-visit" type="date" value={sheetDate} onChange={(e) => setSheetDate(e.target.value)} aria-label="Date the paper is about" />}
            <label className="small" style={{ fontWeight: 400 }}><input type="checkbox" checked={noVisit} onChange={(e) => setNoVisit(e.target.checked)} /> No visit
              (paper that came later, e.g. a mailed lab report): give a date</label></div>}
          {org && <div className="field"><label htmlFor="cs-note">Note (optional)</label>
            <input id="cs-note" maxLength={120} placeholder="e.g. lab reports, make-ups" value={note} onChange={(e) => setNote(e.target.value)} /></div>}
          <div style={{ flex: '0 0 auto', alignSelf: 'end' }}><button type="button" disabled={!ready || busy} onClick={() => void print()}>Print cover sheet</button></div>
        </div>) : (
        <div className="row">
          <div className="field org-picker"><label htmlFor="cs-person">Person</label>
            {person ? <div className="row" style={{ alignItems: 'center', gap: 8 }}><b>{person.lastName}, {person.firstName}</b>
                <span className="muted small">DOB {us(person.dob)}{person.departmentName ? ` · ${person.departmentName}` : ''}</span>
                <button type="button" className="link" onClick={() => { setPerson(null); setFound([]); }}>change</button></div>
              : <><input id="cs-person" type="search" placeholder="Search people: name, ID # or date of birth" value={pq} onChange={(e) => setPq(e.target.value)} autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && found[0] && searchedFor === pq.trim()) { e.preventDefault(); setPerson(found[0]); } }} />
                {found.length > 0 && <ul className="org-picker-hits">{found.map((p) => <li key={p.id}><button type="button" onClick={() => setPerson(p)}>
                  {p.lastName}, {p.firstName} · DOB {us(p.dob)}{p.departmentName ? ` · ${p.departmentName}` : ''}</button></li>)}</ul>}
                {searchedFor && searchedFor === pq.trim() && !found.length && <p className="small muted" style={{ margin: '0.3rem 0 0' }}>No one on file matches “{searchedFor}”.</p>}</>}
          </div>
          {person && <div className="field"><label htmlFor="cs-pvisit">Visit</label>
            <select id="cs-pvisit" value={visit} onChange={(e) => setVisit(e.target.value)}>
              <option value="">No visit (give a date)</option>
              {(visits ?? []).map((a) => <option key={a.id} value={a.id}>{us(a.assessedOn)}{a.departmentName ? ` · ${a.departmentName}` : ''}</option>)}
            </select>
            {!visit && <input type="date" value={sheetDate} onChange={(e) => setSheetDate(e.target.value)} aria-label="Date the paper is about" style={{ marginTop: 6 }} />}</div>}
          {person && <div className="field"><label htmlFor="cs-pnote">Note (optional)</label>
            <input id="cs-pnote" maxLength={120} placeholder="e.g. LabCorp results" value={note} onChange={(e) => setNote(e.target.value)} /></div>}
          <div style={{ flex: '0 0 auto', alignSelf: 'end' }}><button type="button" disabled={!ready || busy} onClick={() => void print()}>Print cover sheet</button></div>
        </div>)}
      </div>


      <div className="card">
        <h2 style={{ marginTop: 0 }}>Recent cover sheets</h2>
        {!recent.data ? <p className="muted">Loading…</p> : !recent.data.length ? <p className="muted">None printed yet.</p> : (
          <div className="table-scroll"><table>
            <thead><tr><th>Code</th><th>Organization or person</th><th>Visit</th><th>Note</th><th>Scanned</th><th>Printed</th><th></th></tr></thead>
            <tbody>{recent.data.map((s) => (
              <tr key={s.id} className={s.voidedAt ? 'muted' : ''}>
                <td className="nowrap"><code>{s.shortCode}</code>{s.voidedAt && <div className="small">voided</div>}</td>
                <td>{s.participantId ? <><Link to={`/participants/${s.participantId}`}>{s.participantName}</Link>{s.departmentName && <div className="muted small">{s.departmentName}</div>}</> : s.departmentName}</td>
                <td className="nowrap">{s.assessmentId && !s.eventId ? <>Visit {us(s.assessedOn ?? null)}</> : s.eventId ? <Link to={`/events/${s.eventId}`}>{visitLabel({ startsOn: s.eventStartsOn!, endsOn: s.eventEndsOn, location: s.eventLocation })}</Link> : <>No visit · {us(s.sheetDate)}</>}</td>
                <td>{s.note}</td>
                <td>{s.usedCount ? `${s.usedCount}×` : <span className="muted">not yet</span>}</td>
                <td className="small">{new Date(s.printedAt).toLocaleString()}{s.printedBy ? ` · ${s.printedBy}` : ''}</td>
                <td className="nowrap"><Link to={`/cover-sheets/print?ids=${s.id}`}>Print again</Link>{!s.voidedAt && <> · <button type="button" className="link" onClick={() => void voidSheet(s)}>Void</button></>}</td>
              </tr>))}</tbody>
          </table></div>)}
      </div>
    </>
  );
}

/** The sheets, one per page; the print dialog opens by itself. */
export function CoverSheetPrint() {
  const [params] = useSearchParams();
  const ids = params.get('ids') ?? '';
  const { data, error } = useData<Sheet[]>(ids ? `/api/cover-sheets/print?ids=${encodeURIComponent(ids)}` : null);
  useEffect(() => { if (data?.length) setTimeout(() => window.print(), 300); }, [data]);
  return (
    <>
      <div className="no-print" style={{ marginBottom: '1rem' }}>
        <p><Link to="/cover-sheets">← Cover sheets</Link></p>
        <ErrorBox error={error} />
        {data && <button type="button" onClick={() => window.print()}>Print {data.length} cover sheet{data.length === 1 ? '' : 's'}</button>}
      </div>
      {data?.map((s) => (
        <section key={s.id} className="cover-sheet">
          <div className="cs-head">NGF Assessments · scan cover sheet</div>
          {/* A person's sheet never prints their name or date of birth: the QR (and the code under it) is how the app knows whose paper it is. */}
          <div className="cs-org">{s.departmentName ?? (s.participantId ? 'Single individual only' : '')}</div>
          {s.participantId && s.departmentName && <div className="cs-visit"><b>Single individual only</b></div>}
          <div className="cs-visit">{s.assessmentId && !s.eventId ? <>Visit {us(s.assessedOn ?? null)}</> : s.eventId ? <>Visit {us(s.eventStartsOn)}{s.eventEndsOn && s.eventEndsOn !== s.eventStartsOn ? `–${us(s.eventEndsOn)}` : ''}{s.eventLocation ? ` · ${s.eventLocation}` : ''}</>
            : <>No visit · {us(s.sheetDate)}</>}</div>
          {s.note && <div className="cs-note">{s.note}</div>}
          <div className="cs-qr" dangerouslySetInnerHTML={{ __html: s.qrSvg ?? '' }} />
          <div className="cs-code">{s.shortCode}</div>
          <div className="cs-help">Put this sheet on top of the pile and scan it with the pile. Every page after it, up to the next cover sheet, is filed under
            {s.participantId ? ' this person' : ' this organization'}{s.eventId || s.assessmentId ? ' and visit' : ''}. One sheet can be used again for more paper of the same kind.</div>
          <div className="cs-foot">Printed {new Date(s.printedAt).toLocaleString()}{s.printedBy ? ` by ${s.printedBy}` : ''}</div>
        </section>))}
    </>
  );
}

interface Pile extends Sheet { batchId: string; batchName: string; scannedAt: string; pages: number; reading: number; toReview: number; filed: number }

/** Scanned piles for a visit or an organization: what came in under each cover sheet and where it stands. */
export function ScanPilesCard({ eventId, departmentId, participantId }: { eventId?: string; departmentId?: string; participantId?: string }) {
  const { me } = useAuth();
  const q = eventId ? `eventId=${eventId}` : participantId ? `participantId=${participantId}` : `departmentId=${departmentId}`;
  const { data } = useData<Pile[]>(`/api/cover-sheets/piles?${q}`);
  if (!data?.length) return null;
  const review = me && canImport(me.role);
  return (
    <div className="card no-print">
      <h2 style={{ marginTop: 0 }}>Scanned paper</h2>
      <table><thead><tr><th>Scanned</th>{!eventId && <th>Visit</th>}<th>Pages</th><th>Status</th><th /></tr></thead>
        <tbody>{data.map((p) => (
          <tr key={`${p.id}-${p.batchId}`}>
            <td className="small">{new Date(p.scannedAt).toLocaleDateString()}{p.note && <div className="muted">{p.note}</div>}</td>
            {!eventId && <td className="small">{p.eventId ? visitLabel({ startsOn: p.eventStartsOn!, endsOn: p.eventEndsOn, location: p.eventLocation }) : `${us(p.sheetDate)} (no visit)`}</td>}
            <td className="small">{p.pages}</td>
            <td className="small">{p.reading > 0 && <span className="badge synced" style={{ marginRight: 4 }}>{p.reading} being read</span>}
              {p.toReview > 0 && <span className="badge reviewed" style={{ marginRight: 4 }}>{p.toReview} to review</span>}
              {p.filed > 0 && <span className="badge signed">{p.filed} filed</span>}</td>
            <td className="small">{review && <Link to={`/imports/${p.batchId}`}>Review</Link>}</td>
          </tr>))}</tbody></table>
    </div>
  );
}

/** The line at the top of an organization's, a visit's or a person's page: the back link, and Print cover sheet at the right (always the
 *  same place), which opens Cover sheets filled in for that page, where more options can be chosen. */
export function CoverSheetTopBar({ back, query }: { back: ReactNode; query: string }) {
  return (
    <div className="spread top-bar no-print">
      <p>{back}</p>
      <Link className="btn secondary" to={`/cover-sheets?${query}`}>Print cover sheet</Link>
    </div>
  );
}
